import produce from 'immer';

export type Regions = {
  id: string;
  templateType: 'regions';
  image: {
    update: (url: string, imageId: string) => void;
    url: string;
  };
  add: () => void;
  hotspots: Array<{
    id: string;
    facetId: string;
    region: number[][];
    delete: () => void;
    position: (region: number[][]) => void;
    text: string;
    updateText: (html: string) => void;
    explanation: string;
    updateExplanation: (html: string) => void;
    open: boolean;
    setOpen: () => void;
  }>;
};

export default function regions({ item, facets, setItem, itemsService }): Regions {
  const addRegion = async () => {
    const tempId = `placeholder-id-${Date.now()}`;

    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.hotspots.push({
          id: tempId,
          text: '',
          explanation: '',
        });
      })
    );

    // TODO: just add it to the page first then save it once they add a hotspot
    const newRegion = await facets.create({
      pathParams: { id: item.id },
      queryParams: {
        include: ['anchor', 'association', 'anchor.image', 'anchor.sound', 'association.image', 'association.sound'],
      },
      data: {
        data: {
          attributes: {
            'quiz-settings': {
              enabled: ['region_reverse_multiple_choice', 'region_reverse_click_in_the_dark'],
            },
          },
          relationships: {
            anchor: {
              data: {
                attributes: {
                  'region-polygon': '0.3 0.3, 0.6 0.3, 0.6 0.6, 0.3 0.6',
                },
              },
            },
            associations: {
              data: {
                attributes: {},
              },
            },
          },
        },
      },
    });

    const newId = newRegion.id;
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        const hotspot = draft.hotspots.find((a) => a.id === tempId);
        hotspot.id = newId;
        hotspot.delete = () => deleteRegion(newId);
        hotspot.position = (region) => setRegionPosition(newId, region);
        hotspot.updateText = (html) => updateText(newId, html);
        hotspot.updateExplanation = (html) => updateExplanation(newId, html);
        hotspot.open = false;
        hotspot.setOpen = () => setOpen(newId);
      })
    );
    setOpen(newId);
  };

  const deleteRegion = (id) => {
    facets.delete({ pathParams: { id } });
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.hotspots = draft.hotspots.filter((a) => a.facetId !== id);
      })
    );
  };
  const setRegionPosition = (facetId: string, region: number[][]) => {
    const regionString = region.map((p) => p.join(' ')).join(', ');

    facets.debouncedUpdate({
      pathParams: { id: facetId },
      data: { data: { relationships: { anchor: { data: { attributes: { 'region-polygon': regionString } } } } } },
      debounceKey: `regions_region_${facetId}`,
    });
    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.hotspots = draft.hotspots.map((h) => (h.facetId === facetId ? { ...h, region } : h));
      })
    );
  };

  const updateImage = (url, imageId) => {
    itemsService.update({
      pathParams: { id: item.id },
      data: { data: { relationships: { image: { data: { id: imageId } } } } },
    });

    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        draft.image.url = url;
      })
    );

    // add an initial region so there is something on the UI
    if (item.facets.length === 0) {
      addRegion();
    }
  };

  const updateText = async (conceptId, html) => {
    facets.debouncedUpdate({
      pathParams: { id: conceptId },
      data: { data: { attributes: { 'association-question-html': String(html) } } },
      debounceKey: `update_text_${conceptId}`,
    });

    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        const hotspotIndex = draft.hotspots.findIndex((a) => a.id === conceptId);
        if (hotspotIndex > -1) {
          draft.hotspots[hotspotIndex].text = html;
        }
      })
    );
  };

  const updateExplanation = async (conceptId, html) => {
    facets.debouncedUpdate({
      pathParams: { id: conceptId },
      data: {
        data: {
          relationships: { association: { data: { attributes: { 'explanation-html': String(html) } } } },
        },
      },
      debounceKey: `update_explanation_${conceptId}`,
    });

    setItem((prevItem) =>
      produce(prevItem, (draft) => {
        const hotspot = draft.hotspots.find((a) => a.id === conceptId);
        if (hotspot) {
          hotspot.explanation = html;
        }
      })
    );
  };

  const setOpen = (id: string) => {
    setItem((prev) =>
      produce(prev, (draft) => {
        draft.hotspots = draft.hotspots.map((hotspot) => ({ ...hotspot, open: hotspot.id === id && !hotspot.open }));
      })
    );
  };

  return {
    id: item.id,
    templateType: 'regions',
    image: {
      update: updateImage,
      url: item.image?.largeUrl || item.image?.mediumUrl,
    },
    add: addRegion,
    hotspots: item.facets.map((facet) => ({
      id: facet.anchor.id,
      facetId: facet.id,
      region: facet.anchor.regionPolygon.split(', ').map((p) => p.split(' ').map(Number)),
      delete: () => deleteRegion(facet.id),
      position: (region) => setRegionPosition(facet.id, region),
      text: facet.associationQuestion,
      updateText: (html) => updateText(facet.id, html),
      explanation: facet.association.explanationHtml,
      updateExplanation: (html) => updateExplanation(facet.id, html),
      open: false,
      setOpen: () => setOpen(facet.anchor.id),
    })),
  };
}
